/* Copyright 2016-2021
   Kabandy
   Author: Cedric Hoarau
 */

import { CustomPropertyDataModel } from "./CustomProperties";
import { QuantityRuleDataModel } from "./QuantityRule";

type ICustomPropertyFile = CustomPropertyDataModel.ICustomPropertyFile;

export module QuantityDataModel {
    //
    // --- Abstract
    interface IAbstractQuantity {
        id: string;
        projectId: string;
        type: EQuantityType;
    }
    interface IAbstractQuantityCopyInfo {
        userId?: string;
        projectIdSrc: string;
        projectIdDest: string;
        idSrc: string;
        idDest?: string;
        type: EQuantityType;
    }
    interface IAbstractQuantityCreateInfo {
        id?: string;
        projectId: string;
        type: EQuantityType;
    }
    interface IAbstractQuantityDeleteInfo {
        userId?: string;
        projectId: string;
        id: string;
        type: EQuantityType;
    }
    interface IAbstractQuantityGetInfo {
        projectId: string;
        id: string;
        type: EQuantityType;
    }

    interface IAbstractQuantityTemplate {
        projectId: string;
        id?: string;
        type: string;
        autoselects: IAutoselect[];
        autoselectType?: EAutoselectType;
        viewMode: EQuantityMode;
        filters: any;
        modelIds: string[];
        modelGroupId: string;
        camera?: any;
    }

    export enum EQuantityMode {
        Physical = "physical",
        Room = "room"
    }

    export enum EQuantityType {
        Measure = "measure",
        Viewpoint = "viewpoint"
    }

    export enum EQuantityUnits {
        Length = "length",
        Surface = "surface",
        Volume = "volume",
        Object = "object",
        Percentdesc = "percentdesc",
        MassKg = "masskg",
        DurationDay = "durationday",
        Euro = "euro"
    }

    export enum EQuantityUnitType {
        Length = "length",
        Surface = "surface",
        Volume = "volume",
        Object = "object"
    }

    export enum EAutoselectType {
        Manual = "manual",
        FromCategory = "from-category"
    }

    export const SelectionMethodFlags = {
        None: 0,
        HasManualSelection: 1 << 0,
        HasAutoselects: 1 << 1,
        HasFreeHandSelection: 1 << 2
    };

    export const WarningsAutoCreationFlags = {
        None: 0,
        ManualSelectionLost: 1 << 0,
        HandFreeSelectionLost: 1 << 1,
        EmptySelectionNoAutoselects: 1 << 2,
        EmptySelectionNoFilters: 1 << 3,
        IncompleteSelection: 1 << 4,
        OldQuantity: 1 << 5
    };

    //
    // --- Filters

    export interface IFilters {
        visibleCriteria: IVisibleCriterion[];
        criteria: ICriterion[];
    }
    export interface IVisibleCriterion {
        id?: string;
        propertyName: string;
    }

    export interface ICriterion {
        id?: string;
        propertyName: string;
        values: string[];
        rules: QuantityRuleDataModel.IRuleRegexParameters[];
    }

    export interface IAutoselect {
        name: string;
        criteria: ICriterion[];
    }

    export enum EQuantityFilename {
        Autoselects = "autoselects.json",
        CustomProperties = "custom-properties.json",
        Filters = "filters.json",
        NonGuidMeasure = "non-guid.measure",
        Selection = "selection.json",
        Snapshot = "snapshot.png"
    }

    export interface IStorageFileCreateInfo {
        filename: EQuantityFilename;
        content?: any;
        contentFilePath?: string;
        contentType: string;
        metadata?: any;
        compressed: boolean;
    }

    //
    // --- Model parameters for template
    export interface IQuantityModelParameters {
        modelgroupId: string;
        modelIds: string[];
        customPropertyStorageId?: string;
        camera?: any;
    }

    //
    // --- Database
    export module Database {
        export interface IQuantity extends IAbstractQuantity {
            content?: any;
            cuttingPlanes?: any;
            snapshot?: any;
            visibleCriteria?: any;
            criteria?: any;
            selectionMethodBitmask?: number; // how the objects were selected
            warningsAutoCreationBitmask?: number; // what could not be reproduced by updating the quantity
        }
        export interface IQuantityCreateInfo extends IAbstractQuantityCreateInfo {
            content: any;
            cuttingPlanes?: any;
            snapshot?: any;
            criteria?: any;
            visibleCriteria?: any;
            selectionMethodBitmask?: number; // how the objects were selected
            warningsAutoCreationBitmask?: number; // what could not be reproduced by updating the quantity
        }
        export interface IQuantityDeleteInfo extends IAbstractQuantityDeleteInfo {}
        export interface IQuantityGetInfo extends IAbstractQuantityGetInfo {}
    }

    //
    // --- Storage

    export module Storage {
        export interface IQuantity extends IAbstractQuantity {
            filters: any;
            selection: any;
            autoselects: IAutoselect[];
            snapshot?: any;
            customProperties?: ICustomPropertyFile;
            nonGuidMeasures?: Buffer;
        }
        export interface IQuantityCopyInfo extends IAbstractQuantityCopyInfo {}
        export interface IQuantityCreateInfo extends IAbstractQuantityCreateInfo {
            fileCreateInfos?: IStorageFileCreateInfo[];
        }
        export interface IQuantityDeleteInfo extends IAbstractQuantityDeleteInfo {}
        export interface IQuantityGetInfo extends IAbstractQuantityGetInfo {}

        export type FilenameKey = keyof typeof EQuantityFilename;

        export type FileUrlMap = {
            [key in FilenameKey]?: string;
        };
    }

    //
    // --- Runtime

    export module Runtime {
        export interface IQuantity extends IAbstractQuantity {
            filters: any;
            selection: any;
            autoselects: IAutoselect[];
            snapshot?: any;
            modelIds: string[];
            modelGroupId: string;
            value: number;
            customProperties?: ICustomPropertyFile;
            nonGuidMeasures?: Buffer;
            selectionMethodBitmask?: number; // how the objects were selected
            warningsAutoCreationBitmask?: number; // what could not be reproduced by updating the quantity
        }
        export interface IQuantityCopyInfo extends IAbstractQuantityCopyInfo {}
        export interface IQuantityCreateInfo extends IAbstractQuantityCreateInfo {
            database: {
                content: any;
                selectionMethodBitmask?: number; // how the objects were selected
                warningsAutoCreationBitmask?: number; // what could not be reproduced by updating the quantity
            };
            storage?: {
                fileCreateInfos: IStorageFileCreateInfo[];
            };
        }
        export interface IQuantityDeleteInfo extends IAbstractQuantityDeleteInfo {}
        export interface IQuantityGetInfo extends IAbstractQuantityGetInfo {}
        export interface IQuantityTemplate extends IAbstractQuantityTemplate {}

        export interface IQuantityTemplateWithRule {
            projectId?: string;
            quantityId?: string;
            quantityType: QuantityDataModel.EQuantityType;
            quantityRule: QuantityRuleDataModel.IAbstractQuantityRule;
            quantityModelParameters: IQuantityModelParameters;
        }
    }
}
